// @flow
import { put, select, takeLatest } from 'redux-saga/effects';
import { put as storePut } from '../../../stores';
import {
  BANNER_ROUTING,
  BANNER_FUNNEL_TOKEN,
  BANNER_INTERMEDIARY_PAGE,
  BANNER_OPEN_URL,
} from '../actions/bannerRoutingActions';
import { fetchECommToken, fetchFunnelLink } from '../services/bannerService';

import {
  getAioDomain,
  getAioFilter,
  getVhisFilter,
  getVhisIntermediaryPageLink,
  getLuckydrawFilter,
} from '../selectors/configSelectors';

import { getCorpWebDomain } from '../selectors/configSelectors';

import _ from 'lodash';
import $url from 'url';
import { getCmsProfile, getLanguage } from '../../../selectors/userSelectors';
import { addCorpWebIndicator } from '../../../utils/emmaUrlConverter';
import { getCorpWebIndicatorConfig } from '../../emma-webview/selectors/webviewSelector';
import { getBaseUrl } from '../../../utils';
import {
  REDIRECT_ACTION,
  REDIRECT_READY,
  IS_WINDOWS_REDAY_CHECK,
} from '../../partners/actions';
import { PostMsgParams } from '../../partners/types/partnerTypes';
import env from '../../../env';
import { OPEN_DISCLAIMER } from '../actions';
import { DEEPLINK_TO_LUCKYDRAW } from '../../ps-luckydraw/actions/deeplinkActions';
import { goto } from '../../../actions/historyActions';
import prismicApiClient from '../../../utils/prismicApi';

function* proceedCorpWebIndicator(url: string) {
  const config = yield select(getCorpWebIndicatorConfig);
  const finalUrl = addCorpWebIndicator({
    url,
    ...config,
  });
  return finalUrl;
}

function* deepLinkToBannerRoutingSaga(action: any) {
  const { payload } = action;
  const { query } = payload;

  const aioList = yield select(getAioFilter);
  const isAio = aioList.some((item: string) => query.landingUrl.includes(item));
  const aioLink = yield select(getAioDomain);
  const vhisList = yield select(getVhisFilter);
  const isVhis = vhisList.some((item: string) =>
    query.landingUrl.includes(item),
  );
  const productType = query.productType as string;
  const luckydrawList: string[] = yield select(getLuckydrawFilter);
  const isLuckydraw = luckydrawList.some((item: string) =>
    query.landingUrl.includes(item),
  );

    // Get promo code from prismicApi
    if (!query.promoCode) {
      query.promoCode = '';
  
      if (query.bannerPrismicId) {
        const cmsProfiles = yield select(getCmsProfile);
        const language = yield select(getLanguage);
  
        const res = yield prismicApiClient.getPromoCode({
          tags: cmsProfiles,
          lang: language,
          bannerId: query.bannerPrismicId
        }).call({
          staleTime: 0,
          cacheTime: 0, // Don't cache promo code
        })
        
        if (res) {
          const code = res.data?.code;
          query.promoCode = code;
        }
      }
    }

  if (isVhis) {
    // If VHIS Intermediary page, redirect to that page directly.
    yield put({
      type: BANNER_INTERMEDIARY_PAGE,
      payload: { query },
    });
  } else if (isAio) {
    yield put({
      type: BANNER_FUNNEL_TOKEN,
      payload: { query, link: `${aioLink}${query.landingUrl}` },
    });
  } else if (isLuckydraw) {
    yield put({
      type: OPEN_DISCLAIMER,
      payload: {
        disclaimerId: 'luckydrawDisclaimer',
        params: null,
        isGoBack: false,
        okActionCallBack: () => {
          storePut({
            type: DEEPLINK_TO_LUCKYDRAW,
          });
        },
      },
    });
  } else if (productType && productType.startsWith('topUp')) {
    // Top up journey doesn't required on web portal
    console.log('Top up promotion found and not support on web portal');
  } else {
    yield put({ type: BANNER_OPEN_URL, payload: { query } });
  }
}

function* openFunnel(action: any) {
  // Open in a new tab to redirect component first so that it can show loading screen.
  const baseUrl = `${getBaseUrl()}/home/partners/banner/redirect`;
  const newTab = window.open(baseUrl) as Window;

  // Get token
  const result = yield fetchECommToken(action);
  let eCommToken = '';
  if (result.status === 200) {
    eCommToken = result.payload.body;
  } else {
    // const errorMessage = yield select(getBannerErrorMessage)
    // const language = yield select(getLanguage);
    return;
  }

  // Get final URL
  const funnel = yield fetchFunnelLink({
    ...action,
    token: eCommToken,
  });
  if (funnel.status === 200) {
    const redirectUrl = funnel.payload.body;

    // Post the final URL to the opened window to redirect
    if (newTab) {
      let checkIsWindowsReadyIntervalId: any;

      // Listener to wait for new windows ready
      const messageEventListener = (event: any) => {
        const postMsgParams: PostMsgParams = event.data;
        if (!postMsgParams || !postMsgParams.action) {
          return;
        }
        switch (postMsgParams.action) {
          // If ready, then post the final url to the new window
          case REDIRECT_READY:
            const PostMsgParams = {
              action: REDIRECT_ACTION,
              targetUrl: redirectUrl,
            };
            // TODO: POST_MESSAGE - May post message to external banner site (* > window.location.origin)
            newTab.postMessage(PostMsgParams, env.POST_MESSAGE_ORIGIN);
            window.removeEventListener('message', messageEventListener);
            clearInterval(checkIsWindowsReadyIntervalId);
            break;
          default:
            break;
        }
      };

      // Listen and wait for new window ready
      window.addEventListener('message', messageEventListener);
      const PostMsgParams = {
        action: IS_WINDOWS_REDAY_CHECK,
      };
      // Interval to ask if new window is ready
      checkIsWindowsReadyIntervalId = setInterval(() => {
        // TODO: POST_MESSAGE - May post message to external banner site (* > window.location.origin)
        newTab.postMessage(PostMsgParams, env.POST_MESSAGE_ORIGIN);
      }, 2000);
    }
  } else {
    // const errorMessage = yield select(getBannerErrorMessage)
    // const language = yield select(getLanguage);
  }
}

function* openIntermediaryPage(action: any) {
  let intermediaryPageLink = yield select(getVhisIntermediaryPageLink);
  const language = yield select(getLanguage);
  intermediaryPageLink = intermediaryPageLink.replace('${language}', language);

  const routingUrl = $url.format({
    pathname: intermediaryPageLink,
    query: action.payload.query.promoCode
      ? {
          promoCode: action.payload.query.promoCode,
        }
      : {},
  });

  const finalUrl = yield proceedCorpWebIndicator(routingUrl);
  if (finalUrl.startsWith(window.origin)) {
    window.open(finalUrl, '_self');
  } else {
    window.open(finalUrl);
  }
}

//** Open a URL normally */
function* openUrl(action: any) {
  const { payload } = action;
  const { query } = payload;

  const systemDomain = yield select(getCorpWebDomain);
  const landingUrl = query.landingUrl;
  const origin =
    landingUrl.toLowerCase().indexOf('http') === 0 ? '' : systemDomain;
  const url = `${origin}${landingUrl}`;
  const finalUrl = yield proceedCorpWebIndicator(url);
  const isDeeplink = landingUrl.includes('EMMA_DEEPLINK');
  if (finalUrl.startsWith(window.origin)) {
    window.open(finalUrl, '_self');
  } else if (isDeeplink) {
    const urlArray = landingUrl.split('EMMA_DEEPLINK:');
    const goToUrl = urlArray[1];
    goto(goToUrl);
  } else {
    window.open(finalUrl);
  }
}

function* bannerRoutingSaga(): any {
  yield takeLatest(BANNER_ROUTING, deepLinkToBannerRoutingSaga);
  yield takeLatest(BANNER_FUNNEL_TOKEN, openFunnel);
  yield takeLatest(BANNER_INTERMEDIARY_PAGE, openIntermediaryPage);
  yield takeLatest(BANNER_OPEN_URL, openUrl);
}

export default bannerRoutingSaga;
